import { isPlatformBrowser } from '@angular/common';
import { Component, ElementRef, inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Category, CmsService, GlobalMessageService, GlobalMessageType, LoggerService, User } from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { Ticket } from '../../../../spartacus/custom/cms-components/myaccount/custom-tickets/model/ticket.model';
import { CustomTicketsService } from '../../../../spartacus/custom/cms-components/myaccount/custom-tickets/services/custom-tickets.service';
import { StateWithTickets, TICKETS_FEATURE } from '../../../../spartacus/custom/cms-components/myaccount/custom-tickets/store/custom-tickets-state';
import { getTicketstListState } from '../../../../spartacus/custom/cms-components/myaccount/custom-tickets/store/selectors/feature-selector';
import { CustomValidators } from '../../../../spartacus/custom/shared/utils/validators/custom-validators';
import { RecaptchaService } from '../../../../spartacus/features/recaptcha/recaptcha.service';
import { ICON_TYPE } from '../../../icons/icon.model';
import { getHttpErrorMessage } from '../../../util/standardize-http-error';
import { CustomOrderReturnService } from './custom-order-return.service';

@Component({
  selector: 'app-custom-order-return',
  templateUrl: './custom-order-return.component.html',
  styleUrls: ['./custom-order-return.component.scss'],
  standalone: false
})
export class CustomOrderReturnComponent implements OnInit, OnDestroy {
  protected subscriptions: Subscription = new Subscription();
  private platformId = inject(PLATFORM_ID);

  constructor(
    protected fb: UntypedFormBuilder,
    protected store: Store<StateWithTickets>,
    protected customTicketsService: CustomTicketsService,
    protected customOrderReturnService: CustomOrderReturnService,
    protected globalMessageService: GlobalMessageService,
    protected route: ActivatedRoute,
    protected userAccount: UserAccountFacade,
    protected recaptchaService: RecaptchaService,
    private cmsService: CmsService
  ) {
    this.customTicketsService.loadTicketsCategory();
    this.categories$ = this.store.pipe(select(getTicketstListState));
    this.user$ = this.userAccount.get();
  }
  @ViewChild('frm') frm?: ElementRef;

  protected logger = inject(LoggerService);
  private validate$ = new BehaviorSubject<boolean>(false);
  private _validate = false;
  categories?: any[];
  mail: string = '';
  user$: Observable<User | undefined>;
  userId: string = '';
  categories$: Observable<StateWithTickets>;
  description?: Observable<string[]>;
  code: string = '';
  validate = this.validate$.asObservable();
  iconTypes = ICON_TYPE;
  fileName = '';
  selectedCategoryCode: any;
  selectedCategoryHasSub = false;
  ticket?: Ticket;
  infobox$?: Observable<any>;
  infoboxVisible = false;
  fileRequired = true;
  orderReturnForm?: UntypedFormGroup;

  protected submitted = new BehaviorSubject<boolean>(false);
  public submitted$ = this.submitted.asObservable();

  ngOnInit(): void {
    this.subscriptions.add(
      this.route.paramMap.subscribe((params) => {
        if (params.get('orderCode') && params.get('mail')) {
          this.code = params.get('orderCode')!;
          this.mail = params.get('mail')!;
        }
      })
    );
    this.infobox$ = this.cmsService.getComponentData('OrderReturnsFormInfobox').pipe((tab) => tab);
    this.resetForm();
    this.orderReturnForm?.controls.ticketOrderCode.setValue(this.code);
    this.orderReturnForm?.controls.email.setValue(this.mail);
    // this.translation.translate('forms.orderReturn.placeholderSelect');
  }

  resetForm(): void {
    this.orderReturnForm = this.fb.group({
      ticketOrderCode: ['', Validators.required],
      issueCategory: ['', Validators.required],
      email: ['', [Validators.required, CustomValidators.emailValidator]],
      subject: ['RETURNORDER', [Validators.required]]
    });
  }

  categorySelected(event: Event): void {
    this.selectedCategoryCode = (event.target as HTMLInputElement).value;
    // this.selectedCategoryCode = event;
    this.selectedCategoryHasSub = this.checkCategoryHasSubCategory(this.selectedCategoryCode);

    this.orderReturnForm?.setControl('c4cSubCategory', this.fb.control('', this._validate && this.selectedCategoryHasSub ? [Validators.required] : [Validators.nullValidator]));
    this.orderReturnForm?.updateValueAndValidity();

    if (!['CA_DEVOL_TOTAL_PEDIDO', 'CA_DEVOL_PARC_PEDIDO'].includes(this.selectedCategoryCode)) {
      this.infoboxVisible = false;
    }

    this.fileRequired = !['CA_INCILOG_RETENTR', 'CA_DOCU_DUPFRAAB', 'CA_DEVOL_TOTAL_PEDIDO', 'CA_DEVOL_PARC_PEDIDO'].includes(this.selectedCategoryCode);
  }

  onSubmit(): void {
    if (!this.orderReturnForm) {
      return;
    }
    if (this.orderReturnForm.valid) {
      this.recaptchaService
        .getToken()
        .then((token) => {
          if (token) {
            this.subscriptions.add(
              this.recaptchaService.validateRecaptchaToken(token).subscribe((response) => {
                if (response?.status === 200) {
                  this.ticket = this.orderReturnForm!.value;
                  if (this.ticket) {
                    this.subscriptions.add(
                      this.customTicketsService.addTicket(this.ticket).subscribe({
                        next: () => {
                          this.showGlobalMessage('forms.contactUs.success', GlobalMessageType.MSG_TYPE_CONFIRMATION);
                          this.validate$.next(false);
                          this._validate = false;
                          this.resetForm();
                        },
                        error: (error) => {
                          const httpErrorMessage = getHttpErrorMessage(error, this.logger);
                          this.globalMessageService.add(httpErrorMessage, GlobalMessageType.MSG_TYPE_ERROR);
                        }
                      })
                    );
                  }
                } else {
                  this.recaptchaService.recaptchaError('forms.recaptcha.invalidToken');
                }
              })
            );
          } else {
            this.recaptchaService.recaptchaError('forms.recaptcha.invalidToken');
          }
        })
        .catch(() => this.recaptchaService.recaptchaError(null));
    } else {
      this.orderReturnForm.markAllAsTouched();
    }
  }

  onValidate(): void {
    this.switchToSubmitted();
    if (this.orderReturnForm?.valid) {
      const ticket: Ticket = this.orderReturnForm?.value;
      this.subscriptions.add(
        this.customOrderReturnService.validateOrderEmail(ticket).subscribe({
          next: (data) => {
            this.validate$.next(true);
            this._validate = true;
            this.orderReturnForm?.setControl('c4cSubCategory', this.fb.control('', this.selectedCategoryHasSub ? [Validators.required] : [Validators.nullValidator]));
            this.orderReturnForm?.setControl('fullName', this.fb.control('', [Validators.required, Validators.maxLength(70), CustomValidators.onlyLetters]));
            this.orderReturnForm?.setControl('phone', this.fb.control('', [Validators.required, CustomValidators.phoneValidator]));
            this.orderReturnForm?.setControl('privacyPolicy', this.fb.control(false, [Validators.requiredTrue]));
            this.orderReturnForm?.setControl('message', this.fb.control('', [Validators.required]));
            this.orderReturnForm?.updateValueAndValidity();
            this.showGlobalMessage('forms.orderReturn.validateform.success', GlobalMessageType.MSG_TYPE_CONFIRMATION);
          }
        })
      );
    } else {
      this.orderReturnForm?.markAllAsTouched();
    }
  }

  checkCategoryHasSubCategory(categoryCode: string): boolean {
    let hasSubCategories = false;
    this.categories$
      .subscribe((state) => {
        hasSubCategories = Boolean(
          state[TICKETS_FEATURE]?.entities?.value?.issueCategories?.find((category: Category) => category?.code === categoryCode)?.subCategory?.length > 0
        );
      })
      .unsubscribe();
    return hasSubCategories;
  }

  protected showGlobalMessage(messageKey: string, messageType: GlobalMessageType): void {
    this.globalMessageService.add({ key: messageKey }, messageType);
  }

  switchToSubmitted() {
    this.submitted.next(true);
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        this.submitted.next(false);
      }, 1500);
    }
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
